import { Box, Stack, Typography } from '@mui/material'
import type { DashboardMetrics } from 'store/dashboard/dashboard.types'
import { ACCENT_BLUE } from 'utils/brand-colors'

type Props = {
  metrics?: DashboardMetrics
  loading?: boolean
}

const SEGMENTS = [
  { key: 'toDeliver' as const, label: 'To Deliver', color: ACCENT_BLUE },
  { key: 'toInvoice' as const, label: 'To Invoice', color: '#D97706' },
  { key: 'notPaid' as const, label: 'Not Paid', color: '#C81E1D' },
]

const amount = (m?: { totalAmount?: number; loadCount?: number }) => ({
  dollars:
    typeof m?.totalAmount === 'number'
      ? m.totalAmount.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
      : '—',
  loads: m?.loadCount ?? 0,
  value: typeof m?.totalAmount === 'number' ? m.totalAmount : Number(m?.totalAmount ?? 0),
})

const FleetOrderDonut = ({ metrics, loading }: Props) => {
  const oi = metrics?.orderInvoices
  const data = SEGMENTS.map((s) => ({
    ...s,
    ...amount(oi?.[s.key]),
  }))
  const total = data.reduce((sum, d) => sum + d.value, 0)

  const size = 152
  const r = 54
  const cx = size / 2
  const cy = size / 2
  const stroke = 16
  const circ = 2 * Math.PI * r

  let offset = 0
  const arcs =
    total > 0
      ? data.map((d) => {
          const len = (d.value / total) * circ
          const arc = { ...d, dash: `${len} ${circ}`, offset: -offset }
          offset += len
          return arc
        })
      : data.map((d, i) => ({
          ...d,
          dash: `${circ / 3} ${circ}`,
          offset: -(i * (circ / 3)),
        }))

  if (loading) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography sx={{ color: '#6B7280', fontSize: 14 }}>Loading…</Typography>
      </Box>
    )
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={3}
      sx={{ py: 0.5, width: 'max-content', minWidth: 280 }}
    >
      <Box
        sx={{
          position: 'relative',
          width: size,
          height: size,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box component="svg" viewBox={`0 0 ${size} ${size}`} sx={{ width: size, height: size }}>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#F3F4F6" strokeWidth={stroke} />
          {arcs.map((a) => (
            <circle
              key={a.key}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={a.color}
              strokeWidth={stroke}
              strokeDasharray={a.dash}
              strokeDashoffset={a.offset}
              strokeLinecap="butt"
              transform={`rotate(-90 ${cx} ${cy})`}
            />
          ))}
        </Box>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>
            {data.reduce((s, d) => s + d.loads, 0)}
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#6B7280', fontWeight: 600, mt: 0.25 }}>Loads</Typography>
        </Box>
      </Box>

      <Stack spacing={1.5} sx={{ justifyContent: 'center', flexShrink: 0, minWidth: 150 }}>
        {data.map((d) => (
          <Stack key={d.key} direction="row" alignItems="center" spacing={1.25} sx={{ minHeight: 40 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: d.color, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827', lineHeight: 1.2 }}>
                {d.label}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#6B7280' }}>{d.loads} Loads</Typography>
            </Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#111827', whiteSpace: 'nowrap' }}>
              {d.dollars}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  )
}

export default FleetOrderDonut
